"use client"

import React from "react"

import { ButtonType, HtmlContentBodyProps } from "@/interface"

const HtmlContentBody: React.FC<HtmlContentBodyProps> = ({ blocks, setHeadings }) => {
  const processHtml = (html: string): string => {
    if (typeof window === "undefined") return html // Prevents server-side execution

    const parser = new DOMParser()
    const doc = parser.parseFromString(html, "text/html")
    const usedIds = new Set<string>()
    const extractedHeadings: { id: string; text: string; depth: number }[] = []

    doc.querySelectorAll("h2, h3").forEach((element, index) => {
      if (!element.id) {
        let headingText =
          element.textContent
            ?.trim()
            .replace(/\s+/g, "-")
            .replace(/[^\w-]/g, "")
            .toLowerCase() || `heading-${index}`

        if (/^\d/.test(headingText)) {
          headingText = `h-${headingText}`
        }

        let uniqueId = headingText
        let count = 1
        while (usedIds.has(uniqueId)) {
          uniqueId = `${headingText}-${count}`
          count++
        }

        element.id = uniqueId
        usedIds.add(uniqueId)
        extractedHeadings.push({
          id: uniqueId,
          text: element.textContent || "",
          depth: element.tagName === "H2" ? 3 : 4,
        })
      }
    })

    doc.querySelectorAll("table").forEach((table) => {
      if (!table.parentElement?.classList.contains("table-wrapper")) {
        const wrapper = doc.createElement("div")
        wrapper.className = "table-wrapper"
        table.parentNode?.insertBefore(wrapper, table)
        wrapper.appendChild(table)
      }
    })

    if (setHeadings) {
      setHeadings(extractedHeadings)
    }

    return doc.body.innerHTML
  }

  return blocks.map((block) => (
    <div key={block.id}>
      {/* Render HTML Block */}
      {block.bodyString && (
        <div dangerouslySetInnerHTML={{ __html: processHtml(block.bodyString) }} className="custom-html-styles" />
      )}

      {/* Render CTA Banner */}
      {block.heading && (
        <div className="my-6 flex flex-col gap-6 rounded-xl bg-[#F5F5F5] bg-[url('/images/svgs/blogsBG/ctaBanner.svg')] bg-cover bg-center bg-no-repeat p-6 md:mx-auto md:my-12 md:w-[400px] md:gap-[18px] md:bg-[url('/images/svgs/blogsBG/ctaBannerContent.svg')] md:p-[17.5px] lg:w-[460px] lg:gap-[18px] lg:p-5 xl:w-[700px] xl:gap-[36px] xl:p-8">
          <div className="flex flex-col text-xl leading-[120%] lg:text-2xl xl:text-[32px]">
            <div className="font-bold tracking-[-4%] text-[#3F3F3F]">{block.heading}</div>
            <div className="font-medium tracking-[-4%] text-[#3F3F3FBF]">{block.subHeading}</div>
          </div>
          {blocks.map((block) => {
            if (Array.isArray(block.buttons)) {
              return block.buttons.map((button, index) => (
                <div key={`${block.id}-${index}`}>
                  <a href={button.buttonURL} className="border">
                    {button.buttonText}
                  </a>
                </div>
              ))
            } else if (block.buttons) {
              const button = block.buttons as ButtonType
              return (
                <div key={block.id} className="flex h-full">
                  <a
                    href={button.buttonURL}
                    className="rounded bg-[#070109] px-4 py-[6px] text-center text-sm font-semibold text-white md:py-[4px] md:text-[9.14px] lg:py-[5px]  lg:text-[10.5px] xl:px-6 xl:py-[12px] xl:text-base"
                  >
                    {button.buttonText}
                  </a>
                </div>
              )
            }
            return null
          })}
        </div>
      )}
    </div>
  ))
}

export default HtmlContentBody
